// Service layer for medical history - no direct Firestore access
// All data access goes through backend APIs

import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

export class MedicalHistoryService {
  /**
   * Get problem list for a patient
   */
  static async getProblemList(patientId, facilityId, options = {}) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/problems?facilityId=${facilityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.problems || [];
    } catch (error) {
      console.error('Error fetching problem list:', error);
      // Return empty array for now since the endpoint might not exist yet
      return [];
    }
  }

  /**
   * Add a new problem to patient's problem list
   */
  static async addProblem(patientId, facilityId, problemData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/problems?facilityId=${facilityId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(problemData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.problem;
    } catch (error) {
      console.error('Error adding problem:', error);
      throw error;
    }
  }

  /**
   * Update an existing problem
   */
  static async updateProblem(patientId, facilityId, problemId, updateData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/problems/${problemId}?facilityId=${facilityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.problem;
    } catch (error) {
      console.error('Error updating problem:', error);
      throw error;
    }
  }

  /**
   * Delete a problem from patient's problem list
   */
  static async deleteProblem(patientId, facilityId, problemId) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/problems/${problemId}?facilityId=${facilityId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting problem:', error);
      throw error;
    }
  }

  /**
   * Get family history for a patient
   */
  static async getFamilyHistory(patientId, facilityId, options = {}) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/family-history?facilityId=${facilityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.familyHistory || [];
    } catch (error) {
      console.error('Error fetching family history:', error);
      // Return empty array for now since the endpoint might not exist yet
      return [];
    }
  }

  /**
   * Add family history item
   */
  static async addFamilyHistory(patientId, facilityId, familyData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/family-history?facilityId=${facilityId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(familyData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.familyHistory;
    } catch (error) {
      console.error('Error adding family history:', error);
      throw error;
    }
  }

  /**
   * Get social history for a patient
   */
  static async getSocialHistory(patientId, facilityId, options = {}) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/social-history?facilityId=${facilityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.socialHistory || {};
    } catch (error) {
      console.error('Error fetching social history:', error);
      // Return empty object for now since the endpoint might not exist yet
      return {};
    }
  }

  /**
   * Update social history
   */
  static async updateSocialHistory(patientId, facilityId, socialData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/social-history?facilityId=${facilityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(socialData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.socialHistory;
    } catch (error) {
      console.error('Error updating social history:', error);
      throw error;
    }
  }

  /**
   * Get surgical history for a patient
   */
  static async getSurgicalHistory(patientId, facilityId, options = {}) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/surgical-history?facilityId=${facilityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.surgicalHistory || [];
    } catch (error) {
      console.error('Error fetching surgical history:', error);
      // Return empty array for now since the endpoint might not exist yet
      return [];
    }
  }

  /**
   * Add surgical history item
   */
  static async addSurgicalHistory(patientId, facilityId, surgicalData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/surgical-history?facilityId=${facilityId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(surgicalData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.surgicalHistory;
    } catch (error) {
      console.error('Error adding surgical history:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive medical history for a patient
   */
  static async getComprehensiveMedicalHistory(patientId, facilityId, options = {}) {
    try {
      const [problems, familyHistory, socialHistory, surgicalHistory] = await Promise.all([
        this.getProblemList(patientId, facilityId, options),
        this.getFamilyHistory(patientId, facilityId, options),
        this.getSocialHistory(patientId, facilityId, options),
        this.getSurgicalHistory(patientId, facilityId, options)
      ]);

      return {
        problems,
        familyHistory,
        socialHistory,
        surgicalHistory
      };
    } catch (error) {
      console.error('Error fetching comprehensive medical history:', error);
      throw error;
    }
  }
}

export default MedicalHistoryService;
